<template>
  <div class="video-account-page">
    <div class="header-section">
      <h2 class="page-title">视频号账号管理</h2>

      <div class="license-info">
        <div class="license-item">
          <span class="label">总授权数：</span>
          <span class="value">{{ licenseInfo.total === -1 ? '无限制' : licenseInfo.total }}</span>
        </div>
        <div class="license-item">
          <span class="label">已使用：</span>
          <span class="value">{{ licenseInfo.used }}</span>
        </div>
        <div class="license-item">
          <span class="label">剩余可用：</span>
          <span class="value">{{ licenseInfo.remaining === -1 ? '无限制' : licenseInfo.remaining }}</span>
        </div>
      </div>
    </div>

    <!-- 搜索区域 -->
    <div class="search-bar">
      <el-input v-model="searchForm.account_name" placeholder="请输入账号名称" clearable size="large"
        style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
        <template #prefix>
          <el-icon>
            <Search />
          </el-icon>
        </template>
      </el-input>

      <el-button type="primary" size="large" @click="handleSearch">
        <el-icon>
          <Search />
        </el-icon> 搜索
      </el-button>

      <el-button type="success" size="large" @click="openAddDialog" class="add-btn">
        <el-icon>
          <Plus />
        </el-icon> 新增账号
      </el-button>

      <el-button type="info" size="large" @click="batchCheckAuthorization" :loading="batchCheckLoading"
        style="margin-left: 15px">
        <el-icon>
          <Refresh />
        </el-icon> 一键检测授权
      </el-button>

      <!-- 极速授权按钮 -->
      <el-button type="primary" size="large" @click="openQuickAuthDialog" style="margin-left: 15px">
        <el-icon>
          <Lightning />
        </el-icon> 极速授权
      </el-button>
    </div>

    <!-- 批量操作栏 -->
    <div class="batch-actions" v-if="selectedIds.length > 0">
      <el-button type="primary" size="large" @click="showBatchProxyDialog">
        <el-icon>
          <Connection />
        </el-icon> 批量设置代理IP (已选 {{ selectedIds.length }} 个)
      </el-button>
      <el-button type="primary" size="large" @click="showBatchCategoryDialog">
        <el-icon>
          <Folder />
        </el-icon> 批量设置分组 (已选 {{ selectedIds.length }} 个)
      </el-button>
      <el-button type="text" @click="clearSelection" style="margin-left: 15px;">
        <el-icon>
          <Close />
        </el-icon> 取消选择
      </el-button>
    </div>

    <!-- 账号列表 -->
    <div class="table-wrapper">
      <el-table ref="accountTable" :data="accountList" stripe v-loading="loading"
        @selection-change="handleSelectionChange" class="account-table">
        <el-table-column type="selection" width="55" />
        <el-table-column prop="account_name" label="账号名称" width="180" />

        <el-table-column label="分组" width="150">
          <template #default="{ row }">
            <el-tag v-if="row.category_name" type="info">{{ row.category_name }}</el-tag>
            <span v-else class="empty-value">未分组</span>
          </template>
        </el-table-column>

        <el-table-column label="授权状态" width="100">
          <template #default="{ row }">
            <el-tag v-if="row.cookies" type="success">已授权</el-tag>
            <el-tag v-else type="danger">未授权</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="授权操作" width="260">
          <template #default="{ row }">
            <div class="auth-buttons">
              <el-button v-if="!row.cookies" type="primary" size="small" @click="startAuthorization(row)">
                扫码授权
              </el-button>
              <el-button v-else type="warning" size="small" @click="startAuthorization(row)">
                重新授权
              </el-button>
              <el-button :disabled="!row.cookies" type="success" size="small" @click="checkAuthorization(row)"
                :loading="checkLoading[row.id]">
                检测授权
              </el-button>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="IP+端口" width="150">
          <template #default="{ row }">
            <span v-if="row.ip_port">{{ row.ip_port }}</span>
            <span v-else class="empty-value">未设置</span>
          </template>
        </el-table-column>

        <el-table-column label="账号密码" width="180">
          <template #default="{ row }">
            <span v-if="row.ip_password" class="password-preview">{{ row.ip_password }}</span>
            <span v-else class="empty-value">未设置</span>
          </template>
        </el-table-column>

        <el-table-column prop="notes" label="备注" width="150" show-overflow-tooltip />
        <el-table-column prop="create_time" label="创建时间" width="160" />

        <el-table-column label="操作" width="220" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon> 编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon> 删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页组件 -->
    <div class="pagination-container">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
        :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
        @size-change="fetchAccountList" @current-change="fetchAccountList" />
    </div>

    <!-- 批量设置代理IP对话框 -->
    <el-dialog v-model="batchProxyDialogVisible" title="批量设置代理IP" width="500px">
      <el-form :model="batchProxyForm" label-width="100px">
        <el-form-item label="IP+端口">
          <el-input v-model="batchProxyForm.ip_port" placeholder="格式：232.33.1.1:16817" />
        </el-form-item>
        <el-form-item label="账号密码">
          <el-input v-model="batchProxyForm.ip_password" placeholder="格式：账号:密码，如：34123:55332" show-password />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="batchProxyDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchProxy" :loading="batchProxyLoading">确认设置</el-button>
      </template>
    </el-dialog>

    <!-- 批量设置分组对话框 -->
    <el-dialog v-model="batchCategoryDialogVisible" title="批量设置分组" width="500px">
      <el-form :model="batchCategoryForm" label-width="100px">
        <el-form-item label="选择分组">
          <el-select v-model="batchCategoryForm.category_id" placeholder="请选择分组" clearable>
            <el-option v-for="category in categoryOptions" :key="category.id" :label="category.account_category"
              :value="category.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="batchCategoryDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchCategory" :loading="batchCategoryLoading">确认设置</el-button>
      </template>
    </el-dialog>

    <!-- 极速授权对话框（只保留视频号） -->
    <el-dialog v-model="quickAuthDialogVisible" title="视频号极速授权" width="500px" class="quick-auth-dialog">
      <div class="quick-auth-container">
        <p class="dialog-tip">点击下方链接即可快速授权，支持扫码绑定账号</p>

        <div class="auth-link-list">
          <!-- 视频号极速授权 -->
          <div class="auth-link-item">
            <div class="link-info">
              <div class="platform-icon video-channel">
                <el-icon>
                  <VideoCamera />
                </el-icon>
              </div>
              <div class="link-content">
                <div class="platform-name">视频号极速授权</div>
                <div class="link-url">{{ quickAuthLink }}</div>
              </div>
            </div>
            <el-button type="primary" size="small" @click="copyToClipboard(quickAuthLink, '视频号授权链接')">
              <el-icon>
                <CopyDocument />
              </el-icon> 复制
            </el-button>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button @click="quickAuthDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 扫码授权对话框 -->
    <el-dialog v-model="qrDialogVisible" title="扫码授权" width="450px">
      <div class="qr-code-container">
        <div v-if="qrLoading" class="loading-container">
          <el-icon class="is-loading" size="30">
            <Loading />
          </el-icon>
          <span>正在获取二维码...</span>
        </div>
        <div v-else>
          <img v-if="qrCodeBase64" :src="qrCodeBase64" alt="授权二维码" class="qr-code-image" />
          <div v-if="showVerificationCode" class="verification-code-section">
            <el-alert title="需要短信验证码" type="warning" :closable="false" style="margin-bottom: 15px;" />
            <el-input v-model="verificationCode" placeholder="请输入验证码" clearable @keyup.enter="confirmAuthorization" />
          </div>
          <div class="qr-instructions">
            <p>请使用视频号APP扫描上方二维码授权账号</p>
            <p>完成后点击下方确认按钮</p>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button @click="qrDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAuthorization" :loading="confirmLoading"
          :disabled="!qrCodeBase64 || (showVerificationCode && !verificationCode)">
          {{ showVerificationCode ? '提交验证码' : '确认授权' }}
        </el-button>
      </template>
    </el-dialog>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px" :close-on-click-modal="false">
      <el-form ref="accountFormRef" :model="accountForm" :rules="formRules" label-width="100px">
        <el-form-item label="账号名称" prop="account_name">
          <el-input v-model="accountForm.account_name" placeholder="请输入账号名称" size="large" />
        </el-form-item>
        <el-form-item label="账号分组">
          <el-select v-model="accountForm.category_id" placeholder="请选择分组" clearable filterable>
            <el-option v-for="category in categoryOptions" :key="category.id" :label="category.account_category"
              :value="category.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="IP+端口">
          <el-input v-model="accountForm.ip_port" placeholder="格式：232.33.1.1:16817（可选）" size="large" />
          <div class="form-tip">非必填项，可留空</div>
        </el-form-item>
        <el-form-item label="账号密码">
          <el-input v-model="accountForm.ip_password" placeholder="格式：账号:密码，如：34123:55332（可选）" size="large"
            show-password />
          <div class="form-tip">非必填项，可留空</div>
        </el-form-item>
        <el-form-item label="备注信息">
          <el-input v-model="accountForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAccountForm" :loading="submitting">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import Cookies from 'js-cookie'
import config from '@/config'
import {
  Search, Plus, Edit, Delete, Refresh, Connection, Folder, Close, Loading,
  Lightning, VideoCamera, CopyDocument
} from '@element-plus/icons-vue'

export default {
  name: 'VideoAccount',
  components: {
    Search, Plus, Edit, Delete, Refresh, Connection, Folder, Close, Loading,
    Lightning, VideoCamera, CopyDocument
  },
  data() {
    return {
      loading: false,
      submitting: false,
      dialogVisible: false,
      dialogTitle: '新增账号',
      isEditMode: false,
      qrDialogVisible: false,
      qrLoading: false,
      confirmLoading: false,
      qrCodeBase64: '',
      showVerificationCode: false,
      verificationCode: '',
      webId: '',
      fingerprint: '',
      currentAuthRow: null,
      batchProxyDialogVisible: false,
      batchProxyLoading: false,
      batchCategoryDialogVisible: false,
      batchCategoryLoading: false,
      batchCheckLoading: false,
      quickAuthDialogVisible: false,
      quickAuthLink: '',
      checkLoading: {},
      selectedIds: [],
      searchForm: {
        account_name: ''
      },
      accountForm: {
        id: null,
        category_id: null,
        account_name: '',
        ip_port: '',
        ip_password: '',
        notes: ''
      },
      formRules: {
        account_name: [{ required: true, message: '请输入账号名称', trigger: 'blur' }]
      },
      batchProxyForm: {
        ip_port: '',
        ip_password: ''
      },
      batchCategoryForm: {
        category_id: null
      },
      accountList: [],
      categoryOptions: [],
      licenseInfo: {
        total: 0,
        used: 0,
        remaining: 0
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    }
  },
  mounted() {
    this.fetchAccountList()
    this.fetchLicenseInfo()
    this.fetchCategories()
    this.buildQuickAuthLink()
  },
  methods: {
    // 构建极速授权链接
    buildQuickAuthLink() {
      const baseUrl = window.location.origin
      const storeId = this.userInfo.id
      this.quickAuthLink = `${baseUrl}/shop/sq.html?store_id=${storeId}&platform=sph`
    },

    // 打开极速授权对话框
    openQuickAuthDialog() {
      this.buildQuickAuthLink()
      this.quickAuthDialogVisible = true
    },

    // 复制到剪贴板
    copyToClipboard(text, name) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        ElMessage.success(`${name}已复制到剪贴板`)
      } catch (err) {
        ElMessage.error('复制失败，请手动复制')
      } finally {
        document.body.removeChild(textarea)
      }
    },

    // 获取账号列表
    async fetchAccountList() {
      this.loading = true
      try {
        const params = {
          action: this.searchForm.account_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        }
        if (this.searchForm.account_name) {
          params.account_name = this.searchForm.account_name
        }
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_account_api.php`, params)
        if (data.data) {
          this.accountList = data.data
          this.pagination.total = data.pagination?.total || data.data.length
        }
      } catch (error) {
        ElMessage.error('获取账号列表失败')
        console.error('获取账号列表错误:', error)
      } finally {
        this.loading = false
      }
    },

    // 获取分组列表
    async fetchCategories() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/account_category_management.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        })
        if (data.data) {
          this.categoryOptions = data.data
        }
      } catch (error) {
        console.error('获取分组列表失败:', error)
      }
    },

    // 获取授权信息
    async fetchLicenseInfo() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_account_api.php`, {
          action: 'license_info',
          store_id: this.userInfo.id
        })
        if (data) {
          this.licenseInfo = {
            total: data.total || 0,
            used: data.used || 0,
            remaining: data.remaining || 0
          }
        }
      } catch (error) {
        console.error('获取授权信息失败:', error)
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.page = 1
      this.fetchAccountList()
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedIds = selection.map(item => item.id)
    },

    // 清空选择
    clearSelection() {
      if (this.$refs.accountTable) {
        this.$refs.accountTable.clearSelection()
      }
      this.selectedIds = []
    },

    // 打开新增对话框
    openAddDialog() {
      if (this.licenseInfo.total !== -1 && this.licenseInfo.remaining <= 0) {
        ElMessage.warning('授权账号数已满，请联系管理员充值！')
        return
      }
      this.dialogTitle = '新增账号'
      this.isEditMode = false
      this.accountForm = {
        id: null,
        category_id: null,
        account_name: '',
        ip_port: '',
        ip_password: '',
        notes: ''
      }
      this.dialogVisible = true
    },

    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑账号'
      this.isEditMode = true
      this.accountForm = {
        id: row.id,
        category_id: row.category_id,
        account_name: row.account_name,
        ip_port: row.ip_port || '',
        ip_password: row.ip_password || '',
        notes: row.notes || ''
      }
      this.dialogVisible = true
    },

    // 提交表单
    submitAccountForm() {
      this.$refs.accountFormRef.validate(async (valid) => {
        if (valid) {
          this.submitting = true
          try {
            const action = this.isEditMode ? 'update' : 'add'
            const payload = { ...this.accountForm, store_id: this.userInfo.id }
            const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_account_api.php`, { action, ...payload })
            if (data.success) {
              ElMessage.success(this.isEditMode ? '更新成功' : '添加成功')
              this.dialogVisible = false
              this.fetchAccountList()
              this.fetchLicenseInfo()
            } else {
              ElMessage.error(data.error || '操作失败')
            }
          } catch (error) {
            ElMessage.error('操作失败')
            console.error('提交错误:', error)
          } finally {
            this.submitting = false
          }
        }
      })
    },

    // 删除账号
    async handleDelete(id) {
      try {
        await ElMessageBox.confirm('确定要删除该账号吗？', '警告', { type: 'warning' })
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_account_api.php`, {
          action: 'delete',
          id: id,
          store_id: this.userInfo.id
        })
        if (data.success) {
          ElMessage.success('删除成功')
          this.fetchAccountList()
          this.fetchLicenseInfo()
        } else {
          ElMessage.error(data.error || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    },

    // 批量设置代理IP
    showBatchProxyDialog() {
      this.batchProxyForm = { ip_port: '', ip_password: '' }
      this.batchProxyDialogVisible = true
    },
    async submitBatchProxy() {
      if (this.selectedIds.length === 0) {
        ElMessage.warning('请先选择要设置的账号')
        return
      }
      this.batchProxyLoading = true
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_account_api.php`, {
          action: 'batch_set_proxy',
          ids: this.selectedIds,
          ip_port: this.batchProxyForm.ip_port,
          ip_password: this.batchProxyForm.ip_password,
          store_id: this.userInfo.id
        })
        if (data.success) {
          ElMessage.success(`成功为 ${data.updated_count} 个账号设置代理`)
          this.batchProxyDialogVisible = false
          this.clearSelection()
          this.fetchAccountList()
        } else {
          ElMessage.error(data.error || '批量设置失败')
        }
      } catch (error) {
        ElMessage.error('批量设置失败')
      } finally {
        this.batchProxyLoading = false
      }
    },

    // 批量设置分组
    showBatchCategoryDialog() {
      this.batchCategoryForm = { category_id: null }
      this.batchCategoryDialogVisible = true
    },
    async submitBatchCategory() {
      if (this.selectedIds.length === 0) {
        ElMessage.warning('请先选择要设置的账号')
        return
      }
      this.batchCategoryLoading = true
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_account_api.php`, {
          action: 'batch_set_category',
          ids: this.selectedIds,
          category_id: this.batchCategoryForm.category_id,
          store_id: this.userInfo.id
        })
        if (data.success) {
          ElMessage.success(`成功为 ${data.updated_count} 个账号设置分组`)
          this.batchCategoryDialogVisible = false
          this.clearSelection()
          this.fetchAccountList()
        } else {
          ElMessage.error(data.error || '批量设置失败')
        }
      } catch (error) {
        ElMessage.error('批量设置失败')
      } finally {
        this.batchCategoryLoading = false
      }
    },

    // 开始授权
    async startAuthorization(row) {
      this.currentAuthRow = row
      this.qrDialogVisible = true
      this.qrLoading = true
      this.qrCodeBase64 = ''
      this.showVerificationCode = false
      this.verificationCode = ''

      try {
        const payload = {
          action: 'get_qr',
          id: row.id,
          store_id: this.userInfo.id
        }
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_auth_api.php`, payload)
        if (data.status === 'success') {
          this.qrCodeBase64 = data.base64
          this.webId = data.web_id
          this.fingerprint = data.fingerprint
        } else {
          ElMessage.error(data.error || '获取二维码失败')
        }
      } catch (error) {
        ElMessage.error('获取二维码失败')
        console.error('获取二维码错误:', error)
      } finally {
        this.qrLoading = false
      }
    },

    // 确认授权
    async confirmAuthorization() {
      this.confirmLoading = true
      try {
        const payload = {
          action: 'update_cookies',
          id: this.currentAuthRow.id,
          web_id: this.webId,
          fingerprint: this.fingerprint,
          store_id: this.userInfo.id
        }
        if (this.showVerificationCode && this.verificationCode) {
          payload.verification_code = this.verificationCode
        }
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/video_auth_api.php`, payload)
        if (data.status === 'success') {
          ElMessage.success('授权成功')
          this.qrDialogVisible = false
          this.fetchAccountList()
        } else if (data.status === 'verification_required') {
          this.showVerificationCode = true
          ElMessage.warning('需要短信验证码')
        } else {
          ElMessage.error(data.error || '授权失败')
        }
      } catch (error) {
        ElMessage.error('授权失败')
        console.error('授权错误:', error)
      } finally {
        this.confirmLoading = false
      }
    },

    async checkAuthorization(row) {
      this.checkLoading = { ...this.checkLoading, [row.id]: true }
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/update_video_cookies.php`, {
          ids: [row.id]
        })
        if (data.success && data.results) {
          const result = data.results[0]
          if (result.status === 'success') {
            ElMessage.success(`账号 ${row.account_name} 授权有效，已更新cookies`)
          } else if (result.status === 'cooldown') {
            ElMessage.warning(`账号 ${row.account_name} 处于冷却期，${result.next_available}后可更新`)
          } else {
            ElMessage.warning(`账号 ${row.account_name} ${result.message}`)
          }
          this.fetchAccountList()
        } else {
          ElMessage.error('检测授权失败')
        }
      } catch (error) {
        ElMessage.error('检测授权失败')
        console.error('检测授权错误:', error)
      } finally {
        this.checkLoading = { ...this.checkLoading, [row.id]: false }
      }
    },

    // 批量检测授权
    async batchCheckAuthorization() {
      const validIds = this.accountList.filter(item => item.cookies).map(item => item.id)
      if (validIds.length === 0) {
        ElMessage.warning('没有可检测的授权账号')
        return
      }
      try {
        await ElMessageBox.confirm(`确定要检测 ${validIds.length} 个账号的授权状态吗？`, '批量检测', { type: 'info' })
      } catch {
        return
      }
      this.batchCheckLoading = true
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/update_video_cookies.php`, { ids: validIds })
        if (data.success) {
          const stats = { success: 0, cooldown: 0, error: 0 }
          data.results.forEach(r => {
            if (r.status === 'success') stats.success++
            else if (r.status === 'cooldown') stats.cooldown++
            else stats.error++
          })
          ElMessage.success(`检测完成: 成功 ${stats.success} 个, 冷却中 ${stats.cooldown} 个, 失败 ${stats.error} 个`)
          this.fetchAccountList()
        } else {
          ElMessage.error('批量检测失败')
        }
      } catch (error) {
        ElMessage.error('批量检测失败')
        console.error('批量检测错误:', error)
      } finally {
        this.batchCheckLoading = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.video-account-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

/* 头部区域样式 */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.license-info {
  display: flex;
  gap: 20px;
  background: #f0f7ff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  flex-wrap: wrap;
}

.license-item {
  display: flex;
  align-items: center;

  .label {
    color: #606266;
    font-weight: 500;
  }

  .value {
    font-weight: 600;
    color: #409eff;
  }
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin: 0;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  flex-wrap: wrap;
  gap: 10px;

  .add-btn {
    margin-left: auto;
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;
}

.account-table {
  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
    white-space: nowrap;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
  
  :deep(.el-table__body-wrapper) {
    overflow-x: auto !important;
    
    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;

      &:hover {
        background: #a8a8a8;
      }
    }
  }
  
  :deep(.el-table__header-wrapper) {
    overflow: hidden !important;
    width: 100% !important;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 对话框样式优化 */
:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

/* 密码预览样式 */
.password-preview {
  font-family: monospace;
  background-color: #f5f7fa;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}

/* 表单提示样式 */
.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}

/* 空值样式 */
.empty-value {
  color: #909399;
  font-style: italic;
}

/* 表单验证提示样式 */
:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}

/* 批量操作栏 */
.batch-actions {
  margin-bottom: 15px;
  padding: 12px 15px;
  background-color: #f0f7ff;
  border-radius: 4px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  border-left: 4px solid #409eff;

  .el-button {
    font-weight: 500;
  }
}

/* 授权按钮组 */
.auth-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

/* 二维码容器样式 */
.qr-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;

    span {
      margin-top: 10px;
    }
  }

  .qr-code-image {
    width: 200px;
    height: 200px;
    margin-bottom: 20px;
    border: 1px solid #eee;
  }

  .qr-instructions {
    margin-top: 15px;
    text-align: center;
    color: #606266;
    font-size: 14px;

    p {
      margin: 5px 0;
    }
  }
}

.verification-code-section {
  margin-top: 15px;
  width: 100%;

  .el-input {
    width: 100%;
  }
}

/* 极速授权对话框样式 */
.quick-auth-dialog {
  :deep(.el-dialog__body) {
    padding: 20px 24px;
  }
}

.quick-auth-container {
  .dialog-tip {
    color: #909399;
    font-size: 14px;
    margin-bottom: 20px;
    text-align: center;
    padding: 8px;
    background: #f5f7fa;
    border-radius: 6px;
  }
}

.auth-link-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.auth-link-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: #f8fafc;
  border-radius: 8px;
  transition: all 0.3s;

  &:hover {
    background: #f0f7ff;
    transform: translateX(5px);
  }

  .link-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    overflow: hidden;
  }

  .platform-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: #07C160;
    color: white;
    font-size: 20px;
  }

  .link-content {
    flex: 1;
    overflow: hidden;

    .platform-name {
      font-weight: 600;
      color: #303133;
      margin-bottom: 4px;
    }

    .link-url {
      font-size: 12px;
      color: #909399;
      word-break: break-all;
    }
  }

  .el-button {
    flex-shrink: 0;
    margin-left: 10px;
  }
}

/* 响应式优化 */
@media (max-width: 768px) {
  .search-bar {
    flex-direction: column;
    align-items: stretch;

    .el-input {
      width: 100% !important;
      margin-right: 0 !important;
    }

    .add-btn {
      margin-left: 0;
    }
  }

  .account-table {
    font-size: 12px;
  }

  .header-section {
    flex-direction: column;
    align-items: flex-start;
  }

  .license-info {
    flex-direction: column;
    gap: 10px;
  }

  .auth-link-item {
    flex-direction: column;
    align-items: flex-start;

    .link-info {
      width: 100%;
    }

    .el-button {
      width: 100%;
      margin-left: 0;
      margin-top: 10px;
    }
  }
}
</style>